/* AMP3 product loops and "See it in action" (Joshua, 4 Oct 2026).
   - Loops (snippets/amp3-loop.liquid): silent clips over the product photo. Nothing is fetched until the page has
     finished loading; a clip only plays while it is on screen. Product cards autoplay when mostly in view, at most
     two at once (Joshua, 4 Oct: "one or two per screen"); hovering a card with a mouse plays it straight away.
     Reduced motion, Save-Data and 2G keep the photos.
   - The pause button on the main product image stops the loop for good (WCAG 2.2.2).
   - "See it in action" jumps the gallery to the product film and starts it. */
(() => {
  if (window.a3LoopReady) return;
  window.a3LoopReady = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const conn = navigator.connection || {};
  const lowData = () => conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const running = [];
  const MAX_CARDS = 2;

  const stop = (v, hide) => {
    v.pause();
    const i = running.indexOf(v);
    if (i > -1) running.splice(i, 1);
    if (hide) v.classList.remove('is-playing');
  };

  const play = (v) => {
    if (reduce.matches || lowData() || v.dataset.userPaused) return;
    if (!v.getAttribute('src')) {
      // Native HLS (Safari, iOS, Android) adapts to the screen and connection; elsewhere the MP4 rendition.
      const hls = v.dataset.srcHls && v.canPlayType('application/vnd.apple.mpegurl');
      v.src = hls ? v.dataset.srcHls : (v.dataset.srcMp4 || v.dataset.srcHls);
    }
    const p = v.play();
    if (p) p.catch(() => {});
    if (!running.includes(v)) running.push(v);
    if (v.dataset.a3Loop === 'card') {
      const cards = running.filter((x) => x.dataset.a3Loop === 'card');
      if (cards.length > MAX_CARDS) stop(cards[0], true);
    }
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const v = e.target;
      const need = v.dataset.a3Loop === 'hero' ? 0.4 : 0.75;
      if (e.isIntersecting && e.intersectionRatio >= need) play(v);
      else stop(v, v.dataset.a3Loop === 'card');
    });
  }, { threshold: [0, 0.4, 0.75] });

  const setup = (root) => {
    root.querySelectorAll('video[data-a3-loop]:not([data-a3-ready])').forEach((v) => {
      v.dataset.a3Ready = '1';
      v.addEventListener('playing', () => v.classList.add('is-playing'));
      if (v.dataset.a3Loop === 'card' && canHover) {
        const card = v.closest('.a3c-card, .a3-card') || v.parentElement;
        card.addEventListener('mouseenter', () => play(v));
      }
      io.observe(v);
    });
    root.querySelectorAll('[data-a3-loop-toggle]').forEach((b) => {
      const v = b.closest('.media-viewer__item')?.querySelector('video[data-a3-loop]');
      if (v && !(reduce.matches || lowData())) b.hidden = false;
    });
  };

  // Pause / play button on the main product image, and "See it in action" (delegated, works before the loops load).
  document.addEventListener('click', (evt) => {
    const toggle = evt.target.closest('[data-a3-loop-toggle]');
    if (toggle) {
      evt.preventDefault();
      evt.stopPropagation();
      const v = toggle.closest('.media-viewer__item')?.querySelector('video[data-a3-loop]');
      if (!v) return;
      if (v.paused) {
        delete v.dataset.userPaused;
        play(v);
        toggle.setAttribute('aria-label', 'Pause the product video');
        toggle.classList.remove('is-paused');
      } else {
        v.dataset.userPaused = '1';
        stop(v);
        toggle.setAttribute('aria-label', 'Play the product video');
        toggle.classList.add('is-paused');
      }
      return;
    }
    const see = evt.target.closest('[data-a3-see-video]');
    if (!see) return;
    evt.preventDefault();
    evt.stopPropagation();
    const gallery = see.closest('media-gallery');
    const item = gallery?.querySelector(`.media-viewer [data-media-id="${see.dataset.a3SeeVideo}"]`);
    if (!gallery || !item) return;
    if (typeof gallery.customSetActiveMedia === 'function') gallery.customSetActiveMedia(item);
    else item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    const Gallery = customElements.get('media-gallery');
    if (Gallery && Gallery.playActiveMedia) Gallery.playActiveMedia(item);
    else item.querySelector('deferred-media')?.loadContent?.();
  }, true);

  const start = () => {
    setup(document);
    new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes.forEach((n) => {
        if (n.nodeType === 1 && (n.matches('video[data-a3-loop]') || n.querySelector('video[data-a3-loop]'))) {
          setup(n.matches('video[data-a3-loop]') ? n.parentElement : n);
        }
      }));
    }).observe(document.body, { childList: true, subtree: true });
  };
  const later = () => ('requestIdleCallback' in window ? window.requestIdleCallback(start, { timeout: 2000 }) : setTimeout(start, 800));
  if (document.readyState === 'complete') later();
  else window.addEventListener('load', later, { once: true });
})();
